import React, { useState, useEffect, useRef, useCallback, useContext, useMemo } from 'react';
import Button from 'src/components/Button';
import Flex from 'src/components/Flex';
import Icon from 'src/components/Icon';
import Input from 'src/components/Input';
import Checkbox from 'src/components/Checkbox';
import DatePicker from 'src/components/DatePicker';
import { Label } from 'src/components/Input';
import { encounterScanSchema } from 'common/lib/model/encounter/encounterScanSchema';
import EncounterStore from 'src/stores/EncounterStore';
import { ToastStoreObject, ToastType } from 'src/stores/ToastStore';
import * as utils from 'src/utils';
import { API_DATE_FORMAT } from 'src/utils/constants';
import { format, parseISO, isValid } from 'date-fns';
import styles from './styles.module.scss';
import type { Encounter } from 'src/utils/types';
import type { DocumentScan } from 'src/queries/useDocumentScans';
import { getEncounterFieldLabel, getEncounterSectionTitle, getEncounterArrayTitle } from 'src/utils/documentScans';

interface DocumentScanEncounterEditorProps {
    encounter: Encounter | null | undefined;
    encounterDataAsset: DocumentScan['assets'][0] | undefined;
    onSave?: () => Promise<void>;
}

function DocumentScanEncounterEditor({ encounter, encounterDataAsset, onSave }: DocumentScanEncounterEditorProps) {
    const encounterStore = useContext(EncounterStore);

    const [isEditMode, setIsEditMode] = useState(false);
    const [editedEncounterData, setEditedEncounterData] = useState<any>(null);
    const [isSaving, setIsSaving] = useState(false);
    const [expandedSections, setExpandedSections] = useState<Set<string>>(
        new Set(['patientInformation', 'encounterInformation', 'accidentInformation'])
    );
    const [expandedArrayItems, setExpandedArrayItems] = useState<Record<string, Set<number>>>({});
    const encounterContentRef = useRef<HTMLDivElement>(null);
    const savedScrollPosition = useRef<number>(0);
    const focusedInputRef = useRef<{ fieldKey: string; scrollTop: number; cursorPosition: number } | null>(null);

    // Initialize edited data when encounter loads
    useEffect(() => {
        if (encounter) {
            setEditedEncounterData(JSON.parse(JSON.stringify(encounter))); // Deep copy
        }
    }, [encounter]);

    // Restore scroll position when edit mode changes
    useEffect(() => {
        if (encounterContentRef.current && savedScrollPosition.current > 0) {
            requestAnimationFrame(() => {
                if (encounterContentRef.current) {
                    encounterContentRef.current.scrollTop = savedScrollPosition.current;
                }
            });
        }
    }, [isEditMode]);

    const toggleSection = useCallback((sectionKey: string) => {
        // Save scroll position before state change
        if (encounterContentRef.current) {
            savedScrollPosition.current = encounterContentRef.current.scrollTop;
        }

        setExpandedSections((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(sectionKey)) {
                newSet.delete(sectionKey);
            } else {
                newSet.add(sectionKey);
            }
            return newSet;
        });

        // Restore scroll position after state update
        requestAnimationFrame(() => {
            if (encounterContentRef.current && savedScrollPosition.current > 0) {
                encounterContentRef.current.scrollTop = savedScrollPosition.current;
            }
        });
    }, []);

    const toggleArrayItem = useCallback((arrayKey: string, index: number) => {
        // Save scroll position before state change
        if (encounterContentRef.current) {
            savedScrollPosition.current = encounterContentRef.current.scrollTop;
        }

        setExpandedArrayItems((prev) => {
            const newState = { ...prev };
            if (!newState[arrayKey]) {
                newState[arrayKey] = new Set<number>();
            }
            const itemSet = new Set(newState[arrayKey]);
            if (itemSet.has(index)) {
                itemSet.delete(index);
            } else {
                itemSet.add(index);
            }
            newState[arrayKey] = itemSet;
            return newState;
        });

        // Restore scroll position after state update
        requestAnimationFrame(() => {
            if (encounterContentRef.current && savedScrollPosition.current > 0) {
                encounterContentRef.current.scrollTop = savedScrollPosition.current;
            }
        });
    }, []);

    const handleSave = async () => {
        if (!encounter || !encounter.enctrId || !editedEncounterData) {
            ToastStoreObject.show('Cannot save: Encounter ID is missing');
            return;
        }

        try {
            setIsSaving(true);
            await encounterStore.updateEncounter(encounter.enctrId, editedEncounterData, true);
            ToastStoreObject.show('Encounter saved successfully', ToastType.Success);

            // Call the onSave callback if provided (e.g., to refresh data)
            if (onSave) {
                await onSave();
            }

            // Exit edit mode
            setIsEditMode(false);
        } catch (error) {
            ToastStoreObject.show(utils.parseError(error));
        } finally {
            setIsSaving(false);
        }
    };

    const handleFieldChange = useCallback((fieldPath: string[], value: any) => {
        // Save current scroll position and field key before state update
        const scrollContainer = encounterContentRef.current;
        const activeElement = document.activeElement as HTMLInputElement;
        const scrollTop = scrollContainer?.scrollTop || 0;
        const fieldKey = fieldPath.join('.');

        // Save cursor position if input is focused
        let cursorPosition = 0;
        if (activeElement && activeElement.tagName === 'INPUT' && activeElement === document.activeElement) {
            cursorPosition = activeElement.selectionStart || 0;
        }

        // Store focus info for restoration
        focusedInputRef.current = { fieldKey, scrollTop, cursorPosition };

        setEditedEncounterData((prev: any) => {
            const updated = JSON.parse(JSON.stringify(prev));
            let current = updated;
            for (let i = 0; i < fieldPath.length - 1; i++) {
                if (!current[fieldPath[i]]) {
                    current[fieldPath[i]] = {};
                }
                current = current[fieldPath[i]];
            }
            current[fieldPath[fieldPath.length - 1]] = value;
            return updated;
        });
    }, []);

    const handleArrayFieldChange = useCallback((arrayPath: string[], index: number, field: string, value: any) => {
        // Save current scroll position and field key before state update
        const scrollContainer = encounterContentRef.current;
        const activeElement = document.activeElement as HTMLInputElement;
        const scrollTop = scrollContainer?.scrollTop || 0;
        const fieldKey = `${arrayPath.join('.')}-${index}-${field}`;

        // Save cursor position if input is focused
        let cursorPosition = 0;
        if (activeElement && activeElement.tagName === 'INPUT' && activeElement === document.activeElement) {
            cursorPosition = activeElement.selectionStart || 0;
        }

        // Store focus info for restoration
        focusedInputRef.current = { fieldKey, scrollTop, cursorPosition };

        setEditedEncounterData((prev: any) => {
            const updated = JSON.parse(JSON.stringify(prev));
            let current = updated;
            for (const path of arrayPath) {
                if (!current[path]) {
                    current[path] = [];
                }
                current = current[path];
            }
            if (!current[index]) {
                current[index] = {};
            }
            current[index][field] = value;
            return updated;
        });
    }, []);

    const handleAddArrayItem = useCallback((arrayPath: string[]) => {
        // Save scroll position before state change
        if (encounterContentRef.current) {
            savedScrollPosition.current = encounterContentRef.current.scrollTop;
        }

        setEditedEncounterData((prev: any) => {
            const updated = JSON.parse(JSON.stringify(prev));
            let current = updated;
            for (const path of arrayPath) {
                if (!current[path]) {
                    current[path] = [];
                }
                current = current[path];
            }
            // Add empty object to array
            current.push({});
            return updated;
        });

        // Restore scroll position after state update
        requestAnimationFrame(() => {
            if (encounterContentRef.current && savedScrollPosition.current > 0) {
                encounterContentRef.current.scrollTop = savedScrollPosition.current;
            }
        });
    }, []);

    const handleDeleteArrayItem = useCallback((arrayPath: string[], index: number) => {
        // Save scroll position before state change
        if (encounterContentRef.current) {
            savedScrollPosition.current = encounterContentRef.current.scrollTop;
        }

        setEditedEncounterData((prev: any) => {
            const updated = JSON.parse(JSON.stringify(prev));
            let current = updated;
            for (const path of arrayPath) {
                if (!current[path]) {
                    current[path] = [];
                }
                current = current[path];
            }
            // Remove item at index
            current.splice(index, 1);
            return updated;
        });

        // Restore scroll position after state update
        requestAnimationFrame(() => {
            if (encounterContentRef.current && savedScrollPosition.current > 0) {
                encounterContentRef.current.scrollTop = savedScrollPosition.current;
            }
        });
    }, []);

    // Restore scroll position and focus after render when field values change
    useEffect(() => {
        if (focusedInputRef.current && encounterContentRef.current) {
            const { fieldKey, scrollTop, cursorPosition } = focusedInputRef.current;

            // Restore scroll position immediately
            encounterContentRef.current.scrollTop = scrollTop;

            // Find and focus the input element by its data attribute
            const inputElement = encounterContentRef.current.querySelector(
                `input[data-field-key="${fieldKey}"]`
            ) as HTMLInputElement;

            if (inputElement) {
                // Use requestAnimationFrame to ensure DOM has updated
                requestAnimationFrame(() => {
                    inputElement.focus();
                    // Restore cursor position
                    const pos = Math.min(cursorPosition, inputElement.value.length);
                    inputElement.setSelectionRange(pos, pos);
                });
            }

            // Clear the ref after restoration
            focusedInputRef.current = null;
        }
    }, [editedEncounterData]);

    if (!encounter) {
        return (
            <div className={styles.encounterPlaceholder}>
                <h2 className={styles.encounterTitle}>Encounter Data</h2>
                <p className={styles.placeholderText}>No encounter data available</p>
            </div>
        );
    }

    // Get the original encounter from the document scan asset
    // Content should be an Encounter object, but handle both object and string cases
    let originalEncounter: any = null;
    if (encounterDataAsset?.content) {
        if (typeof encounterDataAsset.content === 'string') {
            try {
                originalEncounter = JSON.parse(encounterDataAsset.content);
            } catch (e) {
                console.error('Failed to parse encounter content:', e);
            }
        } else {
            originalEncounter = encounterDataAsset.content;
        }
    }

    // Helper function to get a value from an object using a path array
    // Handles both object properties and array indices
    const getValueByPath = (obj: any, path: string[]): any => {
        if (!obj || path.length === 0) return undefined;
        let current = obj;
        for (const key of path) {
            if (current === null || current === undefined) return undefined;
            // Handle array indices - convert string to number if it's a valid array index
            if (Array.isArray(current)) {
                const index = parseInt(key, 10);
                if (!isNaN(index) && index >= 0 && index < current.length) {
                    current = current[index];
                } else {
                    return undefined;
                }
            } else {
                current = current[key];
            }
        }
        return current;
    };

    // Helper function to check if a field value differs from the original
    const getOriginalValue = (fieldPath: string[]): string | null => {
        if (!originalEncounter) {
            if (process.env.NODE_ENV === 'development') {
                console.log('No original encounter found for path:', fieldPath);
            }
            return null;
        }
        const originalValue = getValueByPath(originalEncounter, fieldPath);
        const currentValue = getValueByPath(dataToDisplay, fieldPath);

        // Compare values (convert to strings for comparison)
        const originalStr = originalValue !== null && originalValue !== undefined ? String(originalValue) : '';
        const currentStr = currentValue !== null && currentValue !== undefined ? String(currentValue) : '';

        if (originalStr !== currentStr) {
            return originalStr || null;
        }
        return null;
    };

    // Helper function to normalize values for comparison
    const normalizeValueForComparison = (value: any): string => {
        if (value === null || value === undefined) return '';
        if (typeof value === 'boolean') return value ? 'true' : 'false';
        return String(value).trim();
    };

    // Calculate accuracy percentage - percentage of unchanged properties
    const accuracyPercentage = useMemo(() => {
        if (!originalEncounter) {
            return null;
        }

        // Compare current data (edited if in edit mode, otherwise original encounter) with original
        const currentData = isEditMode ? editedEncounterData : encounter;
        if (!currentData) {
            return null;
        }

        let totalFields = 0;
        let unchangedFields = 0;

        // Helper to compare two values
        const valuesMatch = (original: any, edited: any): boolean => {
            const origStr = normalizeValueForComparison(original);
            const editStr = normalizeValueForComparison(edited);
            return origStr === editStr;
        };

        // Process top-level fields
        const schema = encounterScanSchema.properties;
        for (const [fieldKey, fieldSchema] of Object.entries(schema)) {
            if (fieldSchema.type === 'array') {
                // Handle array fields
                const originalArray = getValueByPath(originalEncounter, [fieldKey]) || [];
                const currentArray = getValueByPath(currentData, [fieldKey]) || [];
                
                // Compare array length
                const maxLength = Math.max(originalArray.length, currentArray.length);
                
                if (maxLength === 0) {
                    // Both arrays are empty - count as 1 unchanged field
                    totalFields += 1;
                    unchangedFields += 1;
                } else {
                    // For each array item, compare all properties
                    for (let i = 0; i < maxLength; i++) {
                        const originalItem = originalArray[i] || {};
                        const currentItem = currentArray[i] || {};
                        
                        // Get item schema
                        if ('items' in fieldSchema && fieldSchema.items && 'properties' in fieldSchema.items) {
                            const itemProperties = (fieldSchema.items as any).properties || {};
                            
                            for (const [itemFieldKey] of Object.entries(itemProperties)) {
                                totalFields += 1;
                                const origValue = originalItem[itemFieldKey];
                                const currentValue = currentItem[itemFieldKey];
                                
                                if (valuesMatch(origValue, currentValue)) {
                                    unchangedFields += 1;
                                }
                            }
                        }
                    }
                }
            } else {
                // Handle regular fields
                totalFields += 1;
                const originalValue = getValueByPath(originalEncounter, [fieldKey]);
                const currentValue = getValueByPath(currentData, [fieldKey]);
                
                if (valuesMatch(originalValue, currentValue)) {
                    unchangedFields += 1;
                }
            }
        }

        if (totalFields === 0) return null;
        
        return parseFloat(((unchangedFields / totalFields) * 100).toFixed(2));
    }, [originalEncounter, editedEncounterData, encounter, isEditMode]);

    const dataToDisplay = isEditMode ? editedEncounterData : encounter;
    const schema = encounterScanSchema.properties;

    // Helper function to check if a field is a date field
    const isDateField = (fieldKey: string): boolean => {
        const dateFieldPatterns = [
            /Dt$/, // Ends with Dt (e.g., admitDt, dischDt)
            /Date$/, // Ends with Date (e.g., authorizationDate)
            /DOB$/, // Ends with DOB (e.g., insuredDOB)
            /Dob$/, // Ends with Dob (e.g., dob)
            /Dttm$/, // Ends with Dttm (e.g., purgedDttm)
            /DateTime$/, // Ends with DateTime (e.g., verificationDateTime)
        ];
        return dateFieldPatterns.some((pattern) => pattern.test(fieldKey));
    };

    // Helper function to check if a field is a time field
    const isTimeField = (fieldKey: string): boolean => {
        const timeFieldPatterns = [
            /Tm$/, // Ends with Tm (e.g., admitTm, dischTm)
            /Time$/, // Ends with Time (e.g., diagTime)
        ];
        return timeFieldPatterns.some((pattern) => pattern.test(fieldKey));
    };

    // Helper function to normalize time string to HH:mm format
    const normalizeTimeValue = (timeValue: any): string => {
        if (!timeValue) return '';
        if (typeof timeValue === 'string') {
            // If already in HH:mm format, return as is
            if (/^\d{2}:\d{2}$/.test(timeValue)) {
                return timeValue;
            }
            // Try to extract time from various formats
            const timeMatch = timeValue.match(/(\d{1,2}):(\d{2})/);
            if (timeMatch) {
                const hours = timeMatch[1].padStart(2, '0');
                const minutes = timeMatch[2];
                return `${hours}:${minutes}`;
            }
        }
        return '';
    };

    // Helper function to convert date string to Date object
    const parseDateValue = (dateValue: any): Date | undefined => {
        if (!dateValue) return undefined;
        if (dateValue instanceof Date) return dateValue;
        if (typeof dateValue === 'string') {
            // Try parsing ISO format first
            const isoDate = parseISO(dateValue);
            if (isValid(isoDate)) return isoDate;
            // Try parsing API format (yyyy-MM-dd)
            try {
                const parsed = parseISO(dateValue);
                if (isValid(parsed)) return parsed;
            } catch (e) {
                // Ignore
            }
        }
        return undefined;
    };

    // Helper function to convert Date object to API date string
    const formatDateForAPI = (date: Date | null | undefined): string | null => {
        if (!date || !isValid(date)) return null;
        return format(date, API_DATE_FORMAT);
    };

    const renderField = (key: string, value: any, path: string[] = []) => {
        const fieldSchema = schema[key as keyof typeof schema];
        if (!fieldSchema) {
            // Debug: log if field schema not found
            if (key === 'patDeceasedInd') {
                console.warn('Field schema not found for:', key, 'Available keys:', Object.keys(schema));
            }
            return null;
        }

        const fieldPath = [...path, key];
        const fieldKey = path.length > 0 ? `${path.join('.')}.${key}` : key;
        const originalValue = getOriginalValue(fieldPath);

        // Check if field is boolean - handle both direct type and nested type properties
        const fieldType = (fieldSchema as any)?.type;
        const isBoolean = fieldType === 'boolean';

        // Handle boolean fields with checkboxes
        if (isBoolean) {
            // Value is already a boolean from the API
            const checked = value === true;
            // getOriginalValue returns a string, so convert it to boolean for comparison
            const originalChecked: boolean | null = originalValue !== null
                ? (originalValue === 'true')
                : null;

            const fieldLabel = getEncounterFieldLabel(key);

            return (
                <div key={fieldKey} className={styles.encounterField} data-testid={`checkbox-field-${key}`}>
                    <div className={styles.checkboxField}>
                        <Checkbox
                            label={fieldLabel}
                            checked={checked}
                            disabled={!isEditMode}
                            onChange={(e) => handleFieldChange(fieldPath, e.target.checked)}
                            data-field-key={fieldKey}
                        />
                    </div>
                    {originalChecked !== null && originalChecked !== checked && (
                        <div className={styles.originalValue}>
                            Original: {originalChecked ? 'true' : 'false'}
                        </div>
                    )}
                </div>
            );
        }

        // Check if field is a date field
        const isDate = isDateField(key);

        // Handle date fields with DatePicker
        if (isDate) {
            const dateValue = parseDateValue(value);

            return (
                <div key={fieldKey} className={styles.encounterField}>
                    <DatePicker
                        label={getEncounterFieldLabel(key)}
                        value={dateValue}
                        disabled={!isEditMode}
                        onChange={(date: Date | null) => {
                            const dateString = formatDateForAPI(date);
                            handleFieldChange(fieldPath, dateString);
                        }}
                        wrapClassName={styles.encounterInput}
                    />
                    {originalValue !== null && (
                        <div className={styles.originalValue}>
                            Original: {originalValue || '(empty)'}
                        </div>
                    )}
                </div>
            );
        }

        // Check if field is a time field
        const isTime = isTimeField(key);

        // Handle time fields with HTML time input
        if (isTime) {
            const timeValue = normalizeTimeValue(value);

            return (
                <div key={fieldKey} className={styles.encounterField}>
                    <Label text={getEncounterFieldLabel(key)} />
                    <input
                        type="time"
                        value={timeValue}
                        disabled={!isEditMode}
                        onChange={(e) => {
                            handleFieldChange(fieldPath, e.target.value);
                        }}
                        className={styles.timeInput}
                        data-field-key={fieldKey}
                    />
                    {originalValue !== null && (
                        <div className={styles.originalValue}>
                            Original: {originalValue || '(empty)'}
                        </div>
                    )}
                </div>
            );
        }

        // Handle non-boolean, non-date, non-time fields with text inputs
        const fieldValue = value !== null && value !== undefined ? String(value) : '';

        return (
            <div key={fieldKey} className={styles.encounterField}>
                <Input
                    label={getEncounterFieldLabel(key)}
                    value={fieldValue}
                    disabled={!isEditMode}
                    onChangeText={(text: string) => handleFieldChange(fieldPath, text)}
                    className={styles.encounterInput}
                    data-field-key={fieldKey}
                />
                {originalValue !== null && (
                    <div className={styles.originalValue}>
                        Original: {originalValue || '(empty)'}
                    </div>
                )}
            </div>
        );
    };

    const renderArrayField = (key: string, value: any, path: string[] = []) => {
        // Handle JSON fields that might be strings or already parsed
        let array: any[] = [];
        if (value) {
            if (typeof value === 'string') {
                try {
                    array = JSON.parse(value);
                } catch (e) {
                    console.error(`Failed to parse ${key} as JSON:`, e);
                    return null;
                }
            } else if (Array.isArray(value)) {
                array = value;
            } else {
                // If it's an object, wrap it in an array
                array = [value];
            }
        }

        // Always show the section, even if empty (so we can add items)
        const sectionKey = key;
        const isExpanded = expandedSections.has(sectionKey);
        const expandedItems = expandedArrayItems[sectionKey] || new Set<number>();

        return (
            <div key={key} className={styles.encounterArraySection}>
                <div
                    className={styles.encounterSectionHeader}
                    onClick={() => toggleSection(sectionKey)}
                >
                    <Icon
                        name={isExpanded ? 'chevron-down' : 'chevron-right'}
                        type="fa"
                        className={styles.chevronIcon}
                    />
                    <h3 className={styles.encounterSectionTitle}>
                        {getEncounterSectionTitle(key)}
                        <span className={styles.itemCount}>({array.length})</span>
                    </h3>
                    {isEditMode && (
                        <Button
                            type="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleAddArrayItem([...path, key]);
                            }}
                            leftIcon={<Icon name="plus" type="fa" />}
                            className={styles.addItemButton}
                        />
                    )}
                </div>
                {isExpanded && (
                    <div className={styles.encounterArrayItems}>
                        {array.length === 0 ? (
                            <div className={styles.emptyArrayMessage}>
                                No items. {isEditMode && 'Click "Add" to create one.'}
                            </div>
                        ) : (
                            array.map((item, index) => {
                                const isItemExpanded = expandedItems.has(index);
                                const itemTitle = getEncounterArrayTitle(key, item, index);
                                return (
                                    <div key={index} className={styles.encounterArrayItem}>
                                        <div
                                            className={styles.encounterArrayItemHeader}
                                            onClick={() => toggleArrayItem(sectionKey, index)}
                                        >
                                            <Icon
                                                name={isItemExpanded ? 'chevron-down' : 'chevron-right'}
                                                type="fa"
                                                className={styles.chevronIcon}
                                            />
                                            <span className={styles.arrayItemTitle}>{itemTitle}</span>
                                            {isEditMode && (
                                                <Button
                                                    type="small"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleDeleteArrayItem([...path, key], index);
                                                    }}
                                                    leftIcon={<Icon name="trash" type="fa" />}
                                                    className={styles.deleteItemButton}
                                                />
                                            )}
                                        </div>
                                        {isItemExpanded && (
                                            <div className={styles.encounterArrayItemContent}>
                                                {Object.keys(item).map((fieldKey) => {
                                                    const uniqueKey = `${key}-${index}-${fieldKey}`;
                                                    const arrayFieldPath = [...path, key, index.toString(), fieldKey];
                                                    const originalValue = getOriginalValue(arrayFieldPath);

                                                    // Check if this is a boolean field by looking at the array item schema
                                                    const arraySchema = schema[key as keyof typeof schema];
                                                    let isBoolean = false;
                                                    if (
                                                        arraySchema &&
                                                        'items' in arraySchema &&
                                                        arraySchema.items &&
                                                        'properties' in arraySchema.items &&
                                                        arraySchema.items.properties
                                                    ) {
                                                        const itemProps = arraySchema.items.properties as Record<string, any>;
                                                        isBoolean = itemProps[fieldKey]?.type === 'boolean';
                                                    }

                                                    // Handle boolean fields with checkboxes
                                                    if (isBoolean) {
                                                        const checked = item[fieldKey] === true || item[fieldKey] === 'true';
                                                        const originalChecked = originalValue !== null
                                                            ? (originalValue === 'true')
                                                            : null;

                                                        return (
                                                            <div key={uniqueKey} className={styles.encounterField}>
                                                                <div className={styles.checkboxField}>
                                                                    <Checkbox
                                                                        label={getEncounterFieldLabel(fieldKey)}
                                                                        checked={checked}
                                                                        disabled={!isEditMode}
                                                                        onChange={(e) => handleArrayFieldChange([...path, key], index, fieldKey, e.target.checked)}
                                                                        data-field-key={uniqueKey}
                                                                    />
                                                                </div>
                                                                {originalChecked !== null && originalChecked !== checked && (
                                                                    <div className={styles.originalValue}>
                                                                        Original: {originalChecked ? 'true' : 'false'}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        );
                                                    }

                                                    // Check if this is a date field
                                                    const isDate = isDateField(fieldKey);

                                                    // Handle date fields with DatePicker
                                                    if (isDate) {
                                                        const dateValue = parseDateValue(item[fieldKey]);

                                                        return (
                                                            <div key={uniqueKey} className={styles.encounterField}>
                                                                <DatePicker
                                                                    label={getEncounterFieldLabel(fieldKey)}
                                                                    value={dateValue}
                                                                    disabled={!isEditMode}
                                                                    onChange={(date: Date | null) => {
                                                                        const dateString = formatDateForAPI(date);
                                                                        handleArrayFieldChange([...path, key], index, fieldKey, dateString);
                                                                    }}
                                                                    wrapClassName={styles.encounterInput}
                                                                />
                                                                {originalValue !== null && (
                                                                    <div className={styles.originalValue}>
                                                                        Original: {originalValue || '(empty)'}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        );
                                                    }

                                                    // Check if this is a time field
                                                    const isTime = isTimeField(fieldKey);

                                                    // Handle time fields with HTML time input
                                                    if (isTime) {
                                                        const timeValue = normalizeTimeValue(item[fieldKey]);

                                                        return (
                                                            <div key={uniqueKey} className={styles.encounterField}>
                                                                <Label text={getEncounterFieldLabel(fieldKey)} />
                                                                <input
                                                                    type="time"
                                                                    value={timeValue}
                                                                    disabled={!isEditMode}
                                                                    onChange={(e) => {
                                                                        handleArrayFieldChange([...path, key], index, fieldKey, e.target.value);
                                                                    }}
                                                                    className={styles.timeInput}
                                                                    data-field-key={uniqueKey}
                                                                />
                                                                {originalValue !== null && (
                                                                    <div className={styles.originalValue}>
                                                                        Original: {originalValue || '(empty)'}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        );
                                                    }

                                                    // Handle non-boolean, non-date, non-time fields with text inputs
                                                    const fieldValue = item[fieldKey] !== null && item[fieldKey] !== undefined ? String(item[fieldKey]) : '';

                                                    return (
                                                        <div key={uniqueKey} className={styles.encounterField}>
                                                            <Input
                                                                label={getEncounterFieldLabel(fieldKey)}
                                                                value={fieldValue}
                                                                disabled={!isEditMode}
                                                                onChangeText={(text: string) => handleArrayFieldChange([...path, key], index, fieldKey, text)}
                                                                className={styles.encounterInput}
                                                                data-field-key={uniqueKey}
                                                            />
                                                            {originalValue !== null && (
                                                                <div className={styles.originalValue}>
                                                                    Original: {originalValue || '(empty)'}
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                );
                            })
                        )}
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className={styles.encounterDataContainer}>
            <div className={styles.encounterHeader}>
                <div className={styles.encounterHeaderLeft}>
                    <h2 className={styles.encounterTitle}>Encounter Data</h2>
                    {accuracyPercentage !== null && (
                        <div className={styles.accuracyBadge}>
                            <span className={styles.accuracyLabel}>Accuracy:</span>
                            <span className={styles.accuracyValue}>{accuracyPercentage}%</span>
                        </div>
                    )}
                </div>
                <Flex direction="row" align="center" className={styles.encounterHeaderButtons}>
                    {isEditMode && (
                        <Button
                            type="primary"
                            onClick={handleSave}
                            text="Save"
                            isLoading={isSaving}
                            leftIcon={<Icon name="check" type="fa" />}
                            disabled={isSaving || !encounter?.enctrId}
                        />
                    )}
                    <Button
                        type="small"
                        onClick={() => {
                            // Save scroll position before state change
                            if (encounterContentRef.current) {
                                savedScrollPosition.current = encounterContentRef.current.scrollTop;
                            }

                            if (isEditMode) {
                                // Reset to original data
                                setEditedEncounterData(JSON.parse(JSON.stringify(encounter)));
                            }
                            setIsEditMode(!isEditMode);

                            // Restore scroll position after state update
                            requestAnimationFrame(() => {
                                if (encounterContentRef.current && savedScrollPosition.current > 0) {
                                    encounterContentRef.current.scrollTop = savedScrollPosition.current;
                                }
                            });
                        }}
                        text={isEditMode ? 'Cancel' : 'Edit'}
                        leftIcon={<Icon name={isEditMode ? 'close' : 'pencil2'} type="fa" />}
                        disabled={isSaving}
                    />
                </Flex>
            </div>

            <div
                ref={encounterContentRef}
                className={styles.encounterContent}
            >
                {/* Patient Information */}
                <div className={styles.encounterSection}>
                    <div
                        className={styles.encounterSectionHeader}
                        onClick={() => toggleSection('patientInformation')}
                    >
                        <Icon
                            name={expandedSections.has('patientInformation') ? 'chevron-down' : 'chevron-right'}
                            type="fa"
                            className={styles.chevronIcon}
                        />
                        <h3 className={styles.encounterSectionTitle}>Patient Information</h3>
                    </div>
                    {expandedSections.has('patientInformation') && (
                        <div className={styles.encounterFieldsGrid}>
                            {renderField('patFrstNm', dataToDisplay.patFrstNm)}
                            {renderField('patMidNm', dataToDisplay.patMidNm)}
                            {renderField('patLastNm', dataToDisplay.patLastNm)}
                            {renderField('patMrn', dataToDisplay.patMrn)}
                            {renderField('patSsn', dataToDisplay.patSsn)}
                            {renderField('patDob', dataToDisplay.patDob)}
                            {renderField('patGenderCd', dataToDisplay.patGenderCd)}
                            {renderField('patZipCd', dataToDisplay.patZipCd)}
                            {renderField('patAddrLn1', dataToDisplay.patAddrLn1)}
                            {renderField('patAddrLn2', dataToDisplay.patAddrLn2)}
                            {renderField('patCity', dataToDisplay.patCity)}
                            {renderField('patStateCd', dataToDisplay.patStateCd)}
                            {renderField('patPhNoHome', dataToDisplay.patPhNoHome)}
                            {renderField('patPhNoBus', dataToDisplay.patPhNoBus)}
                            {renderField('patPhNoMob', dataToDisplay.patPhNoMob)}
                            {renderField('patEmailHome', dataToDisplay.patEmailHome)}
                            {renderField('patEmailBus', dataToDisplay.patEmailBus)}
                            {renderField('patDeceasedInd', dataToDisplay.patDeceasedInd)}
                            {renderField('patDeceasedDt', dataToDisplay.patDeceasedDt)}
                        </div>
                    )}
                </div>

                {/* Encounter Information */}
                <div className={styles.encounterSection}>
                    <div
                        className={styles.encounterSectionHeader}
                        onClick={() => toggleSection('encounterInformation')}
                    >
                        <Icon
                            name={expandedSections.has('encounterInformation') ? 'chevron-down' : 'chevron-right'}
                            type="fa"
                            className={styles.chevronIcon}
                        />
                        <h3 className={styles.encounterSectionTitle}>Encounter Information</h3>
                    </div>
                    {expandedSections.has('encounterInformation') && (
                        <div className={styles.encounterFieldsGrid}>
                            {renderField('enctrNo', dataToDisplay.enctrNo)}
                            {renderField('admitDt', dataToDisplay.admitDt)}
                            {renderField('admitTm', dataToDisplay.admitTm)}
                            {renderField('dischDt', dataToDisplay.dischDt)}
                            {renderField('dischTm', dataToDisplay.dischTm)}
                            {renderField('enctrAdmitTyp', dataToDisplay.enctrAdmitTyp)}
                            {renderField('enctrPatClass', dataToDisplay.enctrPatClass)}
                            {renderField('enctrPatTyp', dataToDisplay.enctrPatTyp)}
                            {renderField('enctrHospSvcCd', dataToDisplay.enctrHospSvcCd)}
                        </div>
                    )}
                </div>

                {/* Accident Information */}
                <div className={styles.encounterSection}>
                    <div
                        className={styles.encounterSectionHeader}
                        onClick={() => toggleSection('accidentInformation')}
                    >
                        <Icon
                            name={expandedSections.has('accidentInformation') ? 'chevron-down' : 'chevron-right'}
                            type="fa"
                            className={styles.chevronIcon}
                        />
                        <h3 className={styles.encounterSectionTitle}>Accident Information</h3>
                    </div>
                    {expandedSections.has('accidentInformation') && (
                        <div className={styles.encounterFieldsGrid}>
                            {renderField('accDt', dataToDisplay.accDt)}
                            {renderField('accCd', dataToDisplay.accCd)}
                            {renderField('accDesc', dataToDisplay.accDesc)}
                            {renderField('accLocn', dataToDisplay.accLocn)}
                            {renderField('accAutoStCd', dataToDisplay.accAutoStCd)}
                            {renderField('accAutoStNm', dataToDisplay.accAutoStNm)}
                            {renderField('accJobRelInd', dataToDisplay.accJobRelInd)}
                            {renderField('accDeathInd', dataToDisplay.accDeathInd)}
                        </div>
                    )}
                </div>

                {/* Insurance */}
                {renderArrayField('enctrInsuranceDoc', dataToDisplay.enctrInsuranceDoc, [])}

                {/* Diagnosis */}
                {renderArrayField('enctrDiagnosisDoc', dataToDisplay.enctrDiagnosisDoc, [])}

                {/* Relations */}
                {renderArrayField('enctrRelationsDoc', dataToDisplay.enctrRelationsDoc, [])}

                {/* Guarantor */}
                {renderArrayField('enctrGuarantorDoc', dataToDisplay.enctrGuarantorDoc, [])}
            </div>
        </div>
    );
}

export default DocumentScanEncounterEditor;

